import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  loadDetailedTeamResults,
  loadTeamRatings,
  loadTeamPlayerRatings,
} from "../utils/dataLoader";
import { determineMatchResult } from "../utils/matchResultHelper";
import { DetailedMatchResult, TeamRating } from "../types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface TeamSummary {
  teamName: string;
  totalMatches: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  currentRating: number;
  ratingChange: number;
  tournamentsPlayed: number[];
  opponents: {
    [opponentName: string]: {
      matches: number;
      wins: number;
      losses: number;
      draws: number;
      winRate: number;
    };
  };
}

const TeamDetails: React.FC = () => {
  const { teamName } = useParams<{ teamName: string }>();
  const navigate = useNavigate();
  const [teamSummary, setTeamSummary] = useState<TeamSummary | null>(null);
  const [teamRating, setTeamRating] = useState<TeamRating | null>(null);
  const [teamPlayerRatings, setTeamPlayerRatings] = useState<any[]>([]);
  const [matches, setMatches] = useState<DetailedMatchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "overview" | "matches" | "opponents"
  >("overview");
  const [expandedOpponents, setExpandedOpponents] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const [allMatches, allRatings, allTeamPlayerRatings] =
          await Promise.all([
            loadDetailedTeamResults(),
            loadTeamRatings(),
            loadTeamPlayerRatings(),
          ]);

        setMatches(allMatches);
        setTeamPlayerRatings(allTeamPlayerRatings);

        const decodedTeamName = decodeURIComponent(teamName || "");

        const team = allRatings.find((t) => t.Team === decodedTeamName);

        if (team) {
          setTeamRating(team);
        }

        const teamMatches = allMatches.filter((m) => {
          const team1Name = Array.isArray(m.team1)
            ? m.team1.join("/")
            : m.team1;
          const team2Name = Array.isArray(m.team2)
            ? m.team2.join("/")
            : m.team2;

          return (
            (team1Name && team1Name === decodedTeamName) ||
            (team2Name && team2Name === decodedTeamName)
          );
        });

        if (teamMatches.length > 0) {
          const summary = generateTeamSummary(decodedTeamName, teamMatches);
          setTeamSummary(summary);
        }
      } catch (error) {
        console.error("Error loading team data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [teamName]);

  const generateTeamSummary = (
    teamName: string,
    teamMatches: any[]
  ): TeamSummary => {
    const summary: TeamSummary = {
      teamName,
      totalMatches: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      winRate: 0,
      currentRating: 0,
      ratingChange: 0,
      tournamentsPlayed: [],
      opponents: {},
    };

    teamMatches.forEach((match) => {
      const team1Name = Array.isArray(match.team1)
        ? match.team1.join("/")
        : match.team1;
      const team2Name = Array.isArray(match.team2)
        ? match.team2.join("/")
        : match.team2;

      const isTeam1 = team1Name === teamName;
      const isTeam2 = team2Name === teamName;

      if (!isTeam1 && !isTeam2) return;

      summary.totalMatches++;

      if (!summary.tournamentsPlayed.includes(match.tournamentNumber)) {
        summary.tournamentsPlayed.push(match.tournamentNumber);
      }

      const opponent = isTeam1 ? team2Name : team1Name;

      if (!summary.opponents[opponent]) {
        summary.opponents[opponent] = {
          matches: 0,
          wins: 0,
          losses: 0,
          draws: 0,
          winRate: 0,
        };
      }

      summary.opponents[opponent].matches++;

      const matchResult = determineMatchResult(match, isTeam1);

      if (matchResult === "W") {
        summary.wins++;
        summary.opponents[opponent].wins++;
      } else if (matchResult === "L") {
        summary.losses++;
        summary.opponents[opponent].losses++;
      } else {
        summary.draws++;
        summary.opponents[opponent].draws++;
      }

      if (isTeam1) {
        summary.currentRating = match.team1RatingAfter;
        summary.ratingChange = match.team1RatingChange;
      } else {
        summary.currentRating = match.team2RatingAfter;
        summary.ratingChange = match.team2RatingChange;
      }
    });

    summary.winRate =
      summary.totalMatches > 0
        ? (summary.wins / summary.totalMatches) * 100
        : 0;

    Object.values(summary.opponents).forEach((opponent) => {
      opponent.winRate =
        opponent.matches > 0 ? (opponent.wins / opponent.matches) * 100 : 0;
    });

    return summary;
  };

  const getRatingBadgeClass = (rating: number): string => {
    if (rating < 1600) return "low";
    if (rating < 1800) return "medium";
    if (rating < 2000) return "high";
    return "very-high";
  };

  const getReversedMatchResult = (matchResult: string): string => {
    if (!matchResult) return "N/A";

    // Handle common match result formats
    if (matchResult.includes("-")) {
      const parts = matchResult.split("-");
      if (parts.length === 2) {
        return `${parts[1]}-${parts[0]}`;
      }
    }

    // Handle other formats by returning the original if we can't parse it
    return matchResult;
  };

  const getReversedSetScores = (setScores: string[]): string[] => {
    if (!setScores || setScores.length === 0) return [];

    return setScores.map((score) => {
      if (score.includes(":")) {
        const parts = score.split(":");
        if (parts.length === 2) {
          return `${parts[1]}:${parts[0]}`;
        }
      }
      return score;
    });
  };

  if (loading) {
    return (
      <div className="card">
        <h2>Loading...</h2>
      </div>
    );
  }

  if (!teamSummary || !teamRating) {
    return (
      <div className="card">
        <h2>Team Not Found</h2>
        <p>The requested team could not be found.</p>
        <Link to="/teams" className="player-link">
          Back to Teams
        </Link>
      </div>
    );
  }

  const teamMatches = matches.filter((m) => {
    const team1Name = Array.isArray(m.team1) ? m.team1.join("/") : m.team1;
    const team2Name = Array.isArray(m.team2) ? m.team2.join("/") : m.team2;

    return (
      (team1Name && team1Name === teamSummary.teamName) ||
      (team2Name && team2Name === teamSummary.teamName)
    );
  });

  return (
    <div>
      <div className="card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <h2>{teamSummary.teamName}</h2>
          <Link to="/teams" className="player-link">
            ← Back to Teams
          </Link>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <h3>Current Rating</h3>
            <p className="value">
              <span
                className={`rating-badge ${getRatingBadgeClass(
                  teamRating.Rating
                )}`}
              >
                {teamRating.Rating}
              </span>
            </p>
          </div>
          <div className="stat-card">
            <h3>Total Matches</h3>
            <p className="value">{teamSummary.totalMatches}</p>
          </div>
          <div className="stat-card">
            <h3>% Wygranych</h3>
            <p
              className="value"
              title={teamSummary.totalMatches > 0
                ? `Wygrane ${((teamSummary.wins / teamSummary.totalMatches) * 100).toFixed(1)}% · Remisy ${((teamSummary.draws / teamSummary.totalMatches) * 100).toFixed(1)}% · Przegrane ${((teamSummary.losses / teamSummary.totalMatches) * 100).toFixed(1)}%`
                : 'Brak meczów'}
            >
              {teamSummary.totalMatches > 0
                ? `${((teamSummary.wins / teamSummary.totalMatches) * 100).toFixed(1)}%`
                : '—'}
            </p>
          </div>
          <div className="stat-card">
            <h3>Tournaments</h3>
            <p className="value">{teamSummary.tournamentsPlayed.length}</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
          <button
            onClick={() => setActiveTab("overview")}
            className={
              activeTab === "overview" ? "btn-primary" : "btn-secondary"
            }
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("matches")}
            className={
              activeTab === "matches" ? "btn-primary" : "btn-secondary"
            }
          >
            Match History
          </button>
          <button
            onClick={() => setActiveTab("opponents")}
            className={
              activeTab === "opponents" ? "btn-primary" : "btn-secondary"
            }
          >
            Opponents
          </button>
        </div>

        {activeTab === "overview" && (
          <div>
            <h3>Team Statistics</h3>
            <div className="table-container">
              <table>
                <tbody>
                  <tr>
                    <td>
                      <strong>Starting Rating:</strong>
                    </td>
                    <td>{teamRating["Starting Rating"]}</td>
                    <td>
                      <strong>Current Rating:</strong>
                    </td>
                    <td>{teamRating.Rating}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Rating Change in Last Tournament:</strong>
                    </td>
                    <td
                      style={{
                        color:
                          teamRating["Rating Difference"] >= 0
                            ? "green"
                            : "red",
                      }}
                    >
                      {teamRating["Rating Difference"] >= 0 ? "+" : ""}
                      {teamRating["Rating Difference"]}
                    </td>
                    <td>
                      <strong>Games Played:</strong>
                    </td>
                    <td>{teamRating["Games Played"]}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Wins:</strong>
                    </td>
                    <td>{teamRating["Games Won"]}</td>
                    <td>
                      <strong>Losses:</strong>
                    </td>
                    <td>{teamRating["Games Lost"]}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Draws:</strong>
                    </td>
                    <td>{teamRating["Games Drawn"]}</td>
                    <td>
                      <strong>% Wygranych:</strong>
                    </td>
                    <td
                      title={teamRating["Games Played"] > 0
                        ? `Wygrane ${((teamRating["Games Won"] / teamRating["Games Played"]) * 100).toFixed(1)}% · Remisy ${((teamRating["Games Drawn"] / teamRating["Games Played"]) * 100).toFixed(1)}% · Przegrane ${((teamRating["Games Lost"] / teamRating["Games Played"]) * 100).toFixed(1)}%`
                        : 'Brak meczów'}
                    >
                      {teamRating["Games Played"] > 0
                        ? `${((teamRating["Games Won"] / teamRating["Games Played"]) * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>First Tournament:</strong>
                    </td>
                    <td>
                      <Link
                        to={`/tournament/${teamRating["First Tournament"]}`}
                        style={{ color: "#2b6cb0", textDecoration: "none" }}
                      >
                        #{teamRating["First Tournament"]}
                      </Link>
                    </td>
                    <td>
                      <strong>Latest Tournament:</strong>
                    </td>
                    <td>
                      <Link
                        to={`/tournament/${teamRating["Latest Tournament"]}`}
                        style={{ color: "#2b6cb0", textDecoration: "none" }}
                      >
                        #{teamRating["Latest Tournament"]}
                      </Link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {teamRating["Rating History"] && teamRating["Rating History"].length > 0 && (
              <div style={{ marginTop: "30px" }}>
                <h3>Rating History</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={teamRating["Rating History"]}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="tournamentNumber"
                      label={{ value: "Tournament Number", position: "insideBottom", offset: -5 }}
                    />
                    <YAxis
                      label={{ value: "Rating", angle: -90, position: "insideLeft" }}
                      domain={["dataMin - 50", "dataMax + 50"]}
                    />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="rating"
                      stroke="#82ca9d"
                      activeDot={{
                        r: 8,
                        cursor: "pointer",
                        onClick: (e: any, payload: any) => {
                          if (payload && payload.payload && payload.payload.tournamentNumber) {
                            navigate(`/tournament/${payload.payload.tournamentNumber}`);
                          }
                        }
                      }}
                      name="Rating"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {activeTab === "matches" && (
          <div>
            <h3>Recent Matches</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Tournament</th>
                    <th>Opponent</th>
                    <th>Opponent Rating</th>
                    <th>Set Scores</th>
                    <th>Score Value</th>
                    <th>Expected Score</th>
                    <th>Rating Before</th>
                    <th>Rating After</th>
                    <th>Rating Change</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMatches
                    .sort((a, b) => {
                      if (b.tournamentNumber !== a.tournamentNumber) {
                        return b.tournamentNumber - a.tournamentNumber;
                      }
                      return b.matchIndex - a.matchIndex;
                    })
                    .map((match, index) => {
                      const isCurrentTeamTeam1 =
                        match.team1 &&
                        (Array.isArray(match.team1)
                          ? match.team1.join("/")
                          : match.team1) === teamSummary.teamName;

                      const ratingBefore =
                        (isCurrentTeamTeam1
                          ? match.team1RatingBefore
                          : match.team2RatingBefore) || 0;
                      const ratingAfter =
                        (isCurrentTeamTeam1
                          ? match.team1RatingAfter
                          : match.team2RatingAfter) || 0;
                      const ratingChange =
                        (isCurrentTeamTeam1
                          ? match.team1RatingChange
                          : match.team2RatingChange) || 0;

                      const opponentName = isCurrentTeamTeam1
                        ? Array.isArray(match.team2)
                          ? match.team2.join("/")
                          : match.team2
                        : Array.isArray(match.team1)
                        ? match.team1.join("/")
                        : match.team1;

                      const opponentNameDisplay = opponentName || "Unknown";

                      // Reverse score and match result if current team is team2
                      const displayScore = isCurrentTeamTeam1
                        ? match.score
                        : 1 - match.score;
                      const displayMatchResult = isCurrentTeamTeam1
                        ? match.matchResult || "N/A"
                        : getReversedMatchResult(match.matchResult || "");
                      const displaySetScores = isCurrentTeamTeam1
                        ? match.setScores
                        : getReversedSetScores(match.setScores || []);

                      // Get opponent rating before the match
                      const opponentRatingBefore = isCurrentTeamTeam1
                        ? match.team2RatingBefore
                        : match.team1RatingBefore;

                      // Get expected score for the current team
                      // Show the expected score for the team's position (similar to other components)
                      const expectedScore = isCurrentTeamTeam1
                        ? match.expectedScore1
                        : match.expectedScore2;

                      return (
                        <tr key={index}>
                          <td>
                            <Link
                              to={`/tournament/${match.tournamentNumber}`}
                              style={{
                                color: "#2b6cb0",
                                textDecoration: "none",
                              }}
                            >
                              {match.tournamentNumber}
                            </Link>
                          </td>
                          <td>
                            <Link
                              to={`/team/${encodeURIComponent(
                                opponentNameDisplay
                              )}`}
                              style={{
                                color: "#2b6cb0",
                                textDecoration: "none",
                              }}
                            >
                              {opponentNameDisplay}
                            </Link>
                          </td>
                          <td>{opponentRatingBefore || "N/A"}</td>
                          <td>
                            {displaySetScores && displaySetScores.length > 0 ? (
                              <span>
                                <strong>{displayMatchResult}</strong>{" "}
                                <span
                                  style={{ fontSize: "0.9rem", color: "#666" }}
                                >
                                  ({displaySetScores.join(" ")})
                                </span>
                              </span>
                            ) : (
                              <span
                                style={{ color: "#999", fontStyle: "italic" }}
                              >
                                No set scores
                              </span>
                            )}
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "0.25rem 0.5rem",
                                borderRadius: "15px",
                                background: "#6c757d",
                                color: "white",
                                fontSize: "0.9rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {displayScore.toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "0.25rem 0.5rem",
                                borderRadius: "15px",
                                background: "#17a2b8",
                                color: "white",
                                fontSize: "0.9rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {expectedScore ? expectedScore.toFixed(2) : "N/A"}
                            </span>
                          </td>
                          <td>{ratingBefore}</td>
                          <td>{ratingAfter}</td>
                          <td
                            style={{
                              color: ratingChange >= 0 ? "green" : "red",
                            }}
                          >
                            {ratingChange >= 0 ? "+" : ""}
                            {ratingChange}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "opponents" && (
          <div>
            <h3>Opponent Statistics</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Opponent</th>
                    <th>Matches</th>
                    <th>Wins</th>
                    <th>Losses</th>
                    <th>Draws</th>
                    <th title="Procent wygranych spotkań. Najedź na wartość żeby zobaczyć remisy/przegrane.">% Wygranych</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(teamSummary.opponents)
                    .sort(([, a], [, b]) => b.matches - a.matches)
                    .map(([opponent, stats]) => {
                      const opponentMatches = matches.filter((m) => {
                        const team1Name = Array.isArray(m.team1)
                          ? m.team1.join("/")
                          : m.team1;
                        const team2Name = Array.isArray(m.team2)
                          ? m.team2.join("/")
                          : m.team2;

                        return (
                          (team1Name &&
                            team1Name === teamSummary.teamName &&
                            team2Name &&
                            team2Name === opponent) ||
                          (team2Name &&
                            team2Name === teamSummary.teamName &&
                            team1Name &&
                            team1Name === opponent)
                        );
                      });
                      const isExpanded = expandedOpponents.has(opponent);

                      return (
                        <React.Fragment key={opponent}>
                          <tr>
                            <td>
                              <Link
                                to={`/team/${encodeURIComponent(opponent)}`}
                                className="player-link"
                              >
                                {opponent}
                              </Link>
                            </td>
                            <td>{stats.matches}</td>
                            <td>{stats.wins}</td>
                            <td>{stats.losses}</td>
                            <td>{stats.draws}</td>
                            <td
                              title={stats.matches > 0
                                ? `Wygrane ${((stats.wins / stats.matches) * 100).toFixed(1)}% · Remisy ${((stats.draws / stats.matches) * 100).toFixed(1)}% · Przegrane ${((stats.losses / stats.matches) * 100).toFixed(1)}%`
                                : 'Brak meczów'}
                            >
                              {stats.matches > 0
                                ? `${((stats.wins / stats.matches) * 100).toFixed(1)}%`
                                : '—'}
                            </td>
                            <td>
                              <button
                                onClick={() => {
                                  const newExpanded = new Set(
                                    expandedOpponents
                                  );
                                  if (isExpanded) {
                                    newExpanded.delete(opponent);
                                  } else {
                                    newExpanded.add(opponent);
                                  }
                                  setExpandedOpponents(newExpanded);
                                }}
                                className="btn-secondary"
                                style={{
                                  padding: "0.25rem 0.5rem",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {isExpanded ? "Hide" : "Show"} Details
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={7}>
                                <div
                                  style={{
                                    padding: "1rem",
                                    backgroundColor: "#f8f9fa",
                                    borderTop: "1px solid #dee2e6",
                                  }}
                                >
                                  <h4
                                    style={{
                                      margin: "0 0 1rem 0",
                                      color: "#495057",
                                    }}
                                  >
                                    Match Details vs {opponent}
                                  </h4>
                                  <div className="table-container">
                                    <table style={{ fontSize: "0.9rem" }}>
                                      <thead>
                                        <tr>
                                          <th>Tournament</th>
                                          <th>Result</th>
                                          <th>Opponent Rating</th>
                                          <th>Score</th>
                                          <th>Rating Before</th>
                                          <th>Rating After</th>
                                          <th>Rating Change</th>
                                          <th>Set Scores</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {opponentMatches
                                          .sort((a, b) => {
                                            if (
                                              b.tournamentNumber !==
                                              a.tournamentNumber
                                            ) {
                                              return (
                                                b.tournamentNumber -
                                                a.tournamentNumber
                                              );
                                            }
                                            return b.matchIndex - a.matchIndex;
                                          })
                                          .map((match, index) => {
                                            const isTeam1 =
                                              match.team1 &&
                                              (Array.isArray(match.team1)
                                                ? match.team1.join("/")
                                                : match.team1) ===
                                                teamSummary.teamName;
                                            const ratingBefore = isTeam1
                                              ? match.team1RatingBefore
                                              : match.team2RatingBefore;
                                            const ratingAfter = isTeam1
                                              ? match.team1RatingAfter
                                              : match.team2RatingAfter;
                                            const ratingChange = isTeam1
                                              ? match.team1RatingChange
                                              : match.team2RatingChange;
                                            const result = isTeam1
                                              ? match.winner === 1
                                                ? "W"
                                                : "L"
                                              : match.winner === 2
                                              ? "W"
                                              : "L";

                                            // Reverse score and match result if current team is team2
                                            const displayScore = isTeam1
                                              ? match.score
                                              : 1 - match.score;
                                            const displayMatchResult = isTeam1
                                              ? match.matchResult || "N/A"
                                              : getReversedMatchResult(
                                                  match.matchResult || ""
                                                );
                                            const displaySetScores = isTeam1
                                              ? match.setScores
                                              : getReversedSetScores(
                                                  match.setScores || []
                                                );

                                            // Get opponent rating before the match
                                            const opponentRatingBefore = isTeam1
                                              ? match.team2RatingBefore
                                              : match.team1RatingBefore;

                                            return (
                                              <tr key={index}>
                                                <td>
                                                  <Link
                                                    to={`/tournament/${match.tournamentNumber}`}
                                                    className="player-link"
                                                  >
                                                    {match.tournamentNumber}
                                                  </Link>
                                                </td>
                                                <td>
                                                  <span
                                                    style={{
                                                      fontWeight: "bold",
                                                      color:
                                                        result === "W"
                                                          ? "green"
                                                          : "red",
                                                    }}
                                                  >
                                                    {result}
                                                  </span>
                                                </td>
                                                <td>
                                                  {opponentRatingBefore ||
                                                    "N/A"}
                                                </td>
                                                <td>
                                                  <span
                                                    style={{
                                                      padding: "0.25rem 0.5rem",
                                                      borderRadius: "15px",
                                                      background: "#6c757d",
                                                      color: "white",
                                                      fontSize: "0.8rem",
                                                      fontFamily: "monospace",
                                                    }}
                                                  >
                                                    {displayScore.toFixed(2)}
                                                  </span>
                                                </td>
                                                <td>{ratingBefore || "N/A"}</td>
                                                <td>{ratingAfter || "N/A"}</td>
                                                <td
                                                  style={{
                                                    color:
                                                      (ratingChange || 0) >= 0
                                                        ? "green"
                                                        : "red",
                                                  }}
                                                >
                                                  {(ratingChange || 0) >= 0
                                                    ? "+"
                                                    : ""}
                                                  {ratingChange || "N/A"}
                                                </td>
                                                <td>
                                                  {displaySetScores &&
                                                  displaySetScores.length >
                                                    0 ? (
                                                    <div>
                                                      <div
                                                        style={{
                                                          fontWeight: "bold",
                                                          marginBottom:
                                                            "0.25rem",
                                                        }}
                                                      >
                                                        {displayMatchResult}
                                                      </div>
                                                      <div
                                                        style={{
                                                          fontSize: "0.8rem",
                                                          color: "#666",
                                                        }}
                                                      >
                                                        {displaySetScores.join(
                                                          " "
                                                        )}
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <span
                                                      style={{
                                                        color: "#999",
                                                        fontStyle: "italic",
                                                      }}
                                                    >
                                                      No set scores
                                                    </span>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Team Players</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Player Name</th>
                <th>Current Rating</th>
                <th>Games Played</th>
                <th>Wins</th>
                <th>Losses</th>
                <th>Draws</th>
                <th title="Procent wygranych spotkań. Najedź na wartość żeby zobaczyć remisy/przegrane.">% Wygranych</th>
                <th>Tournaments</th>
                <th>Rating Change</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Extract unique players from team matches
                const players = new Set<string>();
                teamMatches.forEach((match) => {
                  const team1Name = Array.isArray(match.team1)
                    ? match.team1.join("/")
                    : match.team1;
                  const team2Name = Array.isArray(match.team2)
                    ? match.team2.join("/")
                    : match.team2;

                  if (
                    team1Name === teamSummary.teamName &&
                    Array.isArray(match.team1)
                  ) {
                    match.team1.forEach((player) => players.add(player));
                  } else if (
                    team2Name === teamSummary.teamName &&
                    Array.isArray(match.team2)
                  ) {
                    match.team2.forEach((player) => players.add(player));
                  }
                });

                return Array.from(players)
                  .map((playerName) => {
                    // Find the player's rating data from team player ratings
                    const playerRating = teamPlayerRatings.find(
                      (p) => p.Player === playerName
                    );

                    if (!playerRating) {
                      return null; // Skip if no rating data found
                    }

                    return (
                      <tr key={playerName}>
                        <td>
                          <Link
                            to={`/team-player/${encodeURIComponent(
                              playerName
                            )}`}
                            className="player-link"
                          >
                            <strong>{playerName}</strong>
                          </Link>
                        </td>
                        <td>
                          <span
                            className={`rating-badge ${getRatingBadgeClass(
                              playerRating.Rating
                            )}`}
                          >
                            {playerRating.Rating}
                          </span>
                        </td>
                        <td>{playerRating["Games Played"]}</td>
                        <td>{playerRating["Games Won"]}</td>
                        <td>{playerRating["Games Lost"]}</td>
                        <td>{playerRating["Games Drawn"]}</td>
                        <td
                          title={playerRating["Games Played"] > 0
                            ? `Wygrane ${((playerRating["Games Won"] / playerRating["Games Played"]) * 100).toFixed(1)}% · Remisy ${((playerRating["Games Drawn"] / playerRating["Games Played"]) * 100).toFixed(1)}% · Przegrane ${((playerRating["Games Lost"] / playerRating["Games Played"]) * 100).toFixed(1)}%`
                            : 'Brak meczów'}
                        >
                          {playerRating["Games Played"] > 0
                            ? `${((playerRating["Games Won"] / playerRating["Games Played"]) * 100).toFixed(1)}%`
                            : '—'}
                        </td>
                        <td>{playerRating["Tournaments Played"]}</td>
                        <td
                          style={{
                            color:
                              playerRating["Rating Difference"] >= 0
                                ? "green"
                                : "red",
                          }}
                        >
                          {playerRating["Rating Difference"] >= 0 ? "+" : ""}
                          {playerRating["Rating Difference"]}
                        </td>
                      </tr>
                    );
                  })
                  .filter(Boolean); // Remove null entries
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeamDetails;
